import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

interface Operator {
  _id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  status: 'active' | 'inactive';
  payment_type: 'prepaid' | 'postpaid';
  createdAt: string;
  appStoreLinks?: {
    googlePlay?: string;
    appStore?: string;
    appGallery?: string;
  };
}

interface NewOperator {
  name: string;
  email: string;
  phone: string;
  company: string;
  status: 'active' | 'inactive';
  payment_type: 'prepaid' | 'postpaid';
  password?: string;
  appStoreLinks?: {
    googlePlay?: string;
    appStore?: string;
    appGallery?: string;
  };
}

const Operators: React.FC = () => {
  const { t } = useTranslation();
  const [operators, setOperators] = useState<Operator[]>([]);
  const [newOperator, setNewOperator] = useState<NewOperator>({
    name: '',
    email: '',
    phone: '',
    company: '',
    status: 'active',
    payment_type: 'prepaid',
    appStoreLinks: {
      googlePlay: '',
      appStore: '',
      appGallery: ''
    }
  });
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');

  useEffect(() => {
    fetchOperators();
  }, []);

  const fetchOperators = async () => {
    try {
      setLoading(true);
      const response = await api.get('/operators');
      console.log('Operators response:', response.data);
      setOperators(response.data);
      setError('');
    } catch (error: any) {
      console.error('Detailed fetch error:', error);
      setError(t('operators.messages.fetchError'));
      setOperators([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (operator: Operator) => {
    console.log('Editing operator with data:', operator);
    setIsEditing(operator._id);
    const editData = {
      name: operator.name,
      email: operator.email,
      phone: operator.phone,
      company: operator.company,
      status: operator.status,
      payment_type: operator.payment_type || 'prepaid',
      password: '',
      appStoreLinks: operator.appStoreLinks || {
        googlePlay: '',
        appStore: '',
        appGallery: ''
      }
    };
    console.log('Setting form data:', editData);
    setNewOperator(editData);
    setError('');
    setSuccessMessage('');
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isEditing) return;

    try {
      setLoading(true);
      const updateData: Partial<NewOperator> = {
        name: newOperator.name,
        email: newOperator.email,
        phone: newOperator.phone,
        company: newOperator.company,
        status: newOperator.status,
        payment_type: newOperator.payment_type,
        appStoreLinks: newOperator.appStoreLinks
      };
      
      if (newOperator.password) {
        updateData.password = newOperator.password;
      }

      console.log('Sending update data to API:', updateData);
      const response = await api.put(`/operators/${isEditing}`, updateData);
      console.log('Update response:', response.data);
      
      setSuccessMessage(t('operators.messages.updateSuccess'));
      setIsEditing(null);
      setNewOperator({
        name: '',
        email: '',
        phone: '',
        company: '',
        status: 'active',
        payment_type: 'prepaid',
        appStoreLinks: {
          googlePlay: '',
          appStore: '',
          appGallery: ''
        }
      });
      
      await fetchOperators();
    } catch (error: any) {
      console.error('Update error:', error);
      console.error('Error response:', error.response?.data);
      setError(error.response?.data?.error || t('operators.messages.updateError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isEditing) {
      await handleUpdate(e);
    } else {
      try {
        setLoading(true);
        const submitData = {
          name: newOperator.name,
          email: newOperator.email,
          phone: newOperator.phone,
          company: newOperator.company,
          status: newOperator.status,
          payment_type: newOperator.payment_type,
          password: newOperator.password || 'changeme123',
          appStoreLinks: newOperator.appStoreLinks
        };

        console.log('Sending create data to API:', submitData);
        const response = await api.post('/operators', submitData);
        console.log('Create response:', response.data);
        
        setSuccessMessage(
          t('operators.messages.loginCredentials', {
            email: response.data.email,
            password: submitData.password
          })
        );

        setNewOperator({
          name: '',
          email: '',
          phone: '',
          company: '',
          status: 'active',
          payment_type: 'prepaid',
          appStoreLinks: {
            googlePlay: '',
            appStore: '',
            appGallery: ''
          }
        });
        
        await fetchOperators();
      } catch (error: any) {
        console.error('Create error:', error);
        console.error('Error response:', error.response?.data);
        setError(error.response?.data?.error || t('operators.messages.createError'));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDelete = async (operatorId: string) => {
    if (!window.confirm(t('operators.messages.deleteConfirm'))) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/operators/${operatorId}`);
      setSuccessMessage(t('operators.messages.deleteSuccess'));
      await fetchOperators();
    } catch (error: any) {
      console.error('Delete error:', error);
      const errorMessage = error.response?.data?.error;
      const errorDetails = error.response?.data?.details;
      
      setError(
        errorMessage + 
        (errorDetails ? `\n${errorDetails}` : '')
      );
    } finally {
      setLoading(false);
    }
  };

  // Add logging to the payment type change handler
  const handlePaymentTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newPaymentType = e.target.value as 'prepaid' | 'postpaid';
    console.log('Changing payment type to:', newPaymentType);
    setNewOperator({ 
      ...newOperator, 
      payment_type: newPaymentType
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold mb-6">{t('operators.title')}</h2>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 whitespace-pre-line">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {successMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              value={newOperator.name}
              onChange={(e) => setNewOperator({ ...newOperator, name: e.target.value })}
              placeholder={t('operators.form.name')}
              className="border p-2 rounded"
              required
            />
            <input
              type="email"
              value={newOperator.email}
              onChange={(e) => setNewOperator({ ...newOperator, email: e.target.value })}
              placeholder={t('operators.form.email')}
              className="border p-2 rounded"
              required
            />
            <input
              type="tel"
              value={newOperator.phone}
              onChange={(e) => setNewOperator({ ...newOperator, phone: e.target.value })}
              placeholder={t('operators.form.phone')}
              className="border p-2 rounded"
            />
            <input
              type="text"
              value={newOperator.company}
              onChange={(e) => setNewOperator({ ...newOperator, company: e.target.value })}
              placeholder={t('operators.form.company')}
              className="border p-2 rounded"
              required
            />
            <select
              value={newOperator.status}
              onChange={(e) => setNewOperator({ 
                ...newOperator, 
                status: e.target.value as 'active' | 'inactive' 
              })}
              className="border p-2 rounded"
            >
              <option value="active">{t('operators.form.statusOptions.active')}</option>
              <option value="inactive">{t('operators.form.statusOptions.inactive')}</option>
            </select>
            <select
              value={newOperator.payment_type}
              onChange={handlePaymentTypeChange}
              className="border p-2 rounded"
            >
              <option value="prepaid">{t('operators.form.paymentOptions.prepaid')}</option>
              <option value="postpaid">{t('operators.form.paymentOptions.postpaid')}</option>
            </select>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={newOperator.password || ''}
                onChange={(e) => setNewOperator({ 
                  ...newOperator, 
                  password: e.target.value 
                })}
                placeholder={t('operators.form.password')}
                className="border p-2 rounded w-full"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-2 text-gray-500"
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>

            <div className="col-span-2">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input
                  type="url"
                  value={newOperator.appStoreLinks?.googlePlay || ''}
                  onChange={(e) => setNewOperator({
                    ...newOperator,
                    appStoreLinks: {
                      ...newOperator.appStoreLinks,
                      googlePlay: e.target.value
                    }
                  })}
                  placeholder={t('operators.form.googlePlayLink')}
                  className="border p-2 rounded"
                />
                <input
                  type="url"
                  value={newOperator.appStoreLinks?.appStore || ''}
                  onChange={(e) => setNewOperator({
                    ...newOperator,
                    appStoreLinks: {
                      ...newOperator.appStoreLinks,
                      appStore: e.target.value
                    }
                  })}
                  placeholder={t('operators.form.appStoreLink')}
                  className="border p-2 rounded"
                />
                <input
                  type="url"
                  value={newOperator.appStoreLinks?.appGallery || ''}
                  onChange={(e) => setNewOperator({
                    ...newOperator,
                    appStoreLinks: {
                      ...newOperator.appStoreLinks,
                      appGallery: e.target.value
                    }
                  })}
                  placeholder={t('operators.form.appGalleryLink')}
                  className="border p-2 rounded"
                />
              </div>
            </div>

            <div className="flex items-center mt-4 space-x-4">
              <button
                type="submit"
                disabled={loading}
                className={`bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 ${
                  loading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {loading ? t('operators.messages.saving') : (isEditing ? t('operators.editOperator') : t('operators.addOperator'))}
              </button>

              {isEditing && (
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(null);
                    setNewOperator({
                      name: '',
                      email: '',
                      phone: '',
                      company: '',
                      status: 'active',
                      payment_type: 'prepaid',
                      appStoreLinks: {
                        googlePlay: '',
                        appStore: '',
                        appGallery: ''
                      }
                    });
                    setError('');
                    setSuccessMessage('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  {t('operators.actions.cancel')}
                </button>
              )}
            </div>
          </div>
        </form>

        {loading && <div className="text-center">{t('common.loading')}</div>}

        <div className="overflow-x-auto">
          <table className="min-w-full table-auto">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-4 py-2 text-left">{t('operators.columns.name')}</th>
                <th className="px-4 py-2 text-left">{t('operators.columns.email')}</th>
                <th className="px-4 py-2 text-left">{t('operators.columns.phone')}</th>
                <th className="px-4 py-2 text-left">{t('operators.columns.company')}</th>
                <th className="px-4 py-2 text-left">{t('operators.columns.status')}</th>
                <th className="px-4 py-2 text-left">{t('operators.columns.paymentType')}</th>
                <th className="px-4 py-2 text-left">{t('operators.columns.createdAt')}</th>
                <th className="px-4 py-2 text-left">{t('operators.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {operators.map((operator) => (
                <tr key={operator._id} className="border-t">
                  <td className="px-4 py-2">{operator.name}</td>
                  <td className="px-4 py-2">{operator.email}</td>
                  <td className="px-4 py-2">{operator.phone}</td>
                  <td className="px-4 py-2">{operator.company}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-1 rounded ${
                      operator.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {t(`operators.form.statusOptions.${operator.status}`)}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-1 rounded ${
                      (operator.payment_type || 'prepaid') === 'prepaid'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {t(`operators.form.paymentOptions.${operator.payment_type || 'prepaid'}`)}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    {new Date(operator.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => handleEdit(operator)}
                      className="text-blue-500 hover:text-blue-700 mr-2"
                    >
                      {t('operators.actions.edit')}
                    </button>
                    <button
                      onClick={() => handleDelete(operator._id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      {t('operators.actions.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Operators; 